// submitOrder.js
'use strict';
import fetch from '../resource/customFetch.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import handleFetchError from '../resource/handleFetchError.js';
import { getIsInPwa, getIsIos12 } from '../resource/getUserAgent.js';
import { setupApplePayWithTappay } from '../resource/getApplePayPrimeWithTappay.js';
import { BindEmail } from '../resource/authEntryType.js';
import getMeData from '../selector/getMeData.js';
import getDiamondPackData from '../selector/getDiamondPackData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getOperationData from '../selector/getOperationData.js';
import getModalData from '../selector/getModalData.js';
import addModal from '../action/addModal.js';
import pushToastr from '../action/pushToastr.js';
import removeModals from './removeModals.js';
import fetchAccountLinks from '../action/fetchAccountLinks.js';
import openBindEmailModal from '../action/openBindEmailModal.js';
import {
  DIAMOND_SHOP_METHOD_TYPES_BLACKLIST_TIMEOUT_SECS,
  TAPPAY_APPLE_PAY_ID,
  ECPAY_APPLE_PAY_MERCHANT_ID,
  FEATURE_SHOP_UI_2025MAR,
  SHOP_CHECKOUT_VERSION,
} from '../RemoteConfigKeys.js';
import {
  MERGE_OPERATION_DATA,
  ADD_ORDERS,
  SET_NETWORKING_IDLE,
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
  MERGE_ORDER_DATA,
} from '../ActionTypes.js';
import getTimestampOffset, {
  Accuracy,
} from '../selector/getTimestampOffset.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import getIsSupportApplePay, {
  checkApplePayActiveCard,
} from '../resource/getIsSupportApplePay.js';
import trackMixpanel, { EventTypes } from '../resource/mixpanel.js';
import getFpIdPro from '../action/getFpIdPro.js';
import { FEATURE_FINGERPRINT_PRO_SHOP } from '../RemoteConfigKeys.js';
import { getPaymentSourceJointId } from '../resource/shopUtils.js';
import {
  getIsApplePay,
  getIsTappayApplePay,
  getIsEcpayApplePay,
  getIsNineoneappApplePay,
} from '../resource/shopUtils.js';
import { breakpoint } from '../style/variables.js';

/**
 * Create order
 * @kind action
 * @param {string} {shopId} - shop id [ 'addvalue', 'messagePack' ].
 * @param {array} {productIds} - array of product ids (skus).
 * @param {string} [{currency}] - currency.
 * @param {string} [{email}] - user email.
 * @param {object} {source} - Payment Source (optional).
 * @param {boolean} {mustSelectPaymentSource} - requires at least one payment source.
 * @param {boolean} {isFromExpressPayment} - is from express payment related actions.
 * @return {Promise} Action promise.
 */
const submitOrder =
  ({
    shopId,
    productIds,
    currency,
    email,
    source,
    mustSelectPaymentSource,
    isFromExpressPayment = false,
  }) =>
  async (dispatch, getState) => {
    const state = getState();
    const token = getMeData(state, 'token');
    if (!token) {
      return { type: '' };
    }
    const isEcpayApplePayOpened =
      getModalData(state, `${shopId}.EcpayApplePay`, 'zIndex') != null;
    if (isEcpayApplePayOpened) {
      // Remove the EcpayApplePay modal first, ensure that the ECPayApplePay SDK can be reloaded correctly
      dispatch(
        removeModals({
          ids: [`${shopId}.EcpayApplePay`],
        })
      );
    }

    const is2025MarShopUi =
      getRemoteConfigData(state, FEATURE_SHOP_UI_2025MAR) === 1;

    const shouldBindEmail = productIds.find(diamondPackId => {
      return getDiamondPackData(state, diamondPackId, 'modifiers')
        ?.emailRequired;
    });

    if (shouldBindEmail) {
      await dispatch(fetchAccountLinks());
      const isEmailBound = getMeData(state, 'isEmailBound');
      if (!isEmailBound) {
        return dispatch(
          openBindEmailModal({
            canClose: true,
            entry: BindEmail.ACCOUNT_LINKS,
          })
        );
      }
    }

    const storedEmail = getMeData(state, 'email');
    const selectPath = ['shop', shopId, 'submitOrder'];
    const operationSelectPath = ['shop', shopId];

    const url = getResourceUrl({ endpoint: '/shop/checkout' });

    const shopCheckoutVersion = getRemoteConfigData(
      getState(),
      SHOP_CHECKOUT_VERSION
    );
    if (shopCheckoutVersion) {
      url.searchParams.set('v', shopCheckoutVersion);
    }

    const fetchOptions = {
      method: 'POST',
      headers: {
        ...getHeaders(),
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        skus: productIds,
        email: email || storedEmail,
        currency,
        ...(source && {
          source,
        }),
      }),
    };

    dispatch({ type: SET_NETWORKING_FETCHING, payload: { selectPath } });

    const isFpProEnabled =
      getRemoteConfigData(getState(), FEATURE_FINGERPRINT_PRO_SHOP) === 1;
    if (isFpProEnabled) {
      try {
        await dispatch(getFpIdPro({ via: 'payment.succeeded' }));
        const fpIdPro = getOperationData(
          getState(),
          ['fingerprint'],
          'fpIdPro'
        );
        if (fpIdPro) {
          fetchOptions.headers['X-Fingerprint-Pro-Id'] = fpIdPro;
        }
      } catch {
        // silent fail - don't block order creation
      }
    }

    let payload = undefined;

    // 200: free diamond, 201: pay diamond
    let statusCode = undefined;

    try {
      let response = await fetch(url.href, fetchOptions);

      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl: url,
        });
      }

      statusCode = response.status;
      payload = await response.json();

      // clear previous payment options.
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: operationSelectPath,
          data: {
            submitOrderId: '',
            selectPaymentSourceJointId: '',
          },
        },
      });

      if (payload.currency) {
        payload.currency = payload.currency.toUpperCase();
      }

      trackMixpanel({
        type: EventTypes.ORDER_CREATION_SUCCESS,
        payload,
      });

      if (statusCode === 201) {
        if (!is2025MarShopUi) {
          dispatch(
            addModal({
              id: `${shopId}.OrderSummary`,
              isHigherThanAll: true,
            })
          );
        }
      }
    } catch (error) {
      // clear previous payment options.
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: operationSelectPath,
          data: {
            submitOrderId: '',
            selectPaymentSourceJointId: '',
          },
        },
      });

      let textKey = '';
      try {
        textKey = JSON.parse(error.message)
          .code.replace(/\W/g, '_')
          .toLowerCase();
        // eslint-disable-next-line no-empty
      } catch (_) {}

      if (textKey) {
        dispatch(pushToastr({ textKey, color: 'error' }));
      }

      trackMixpanel({
        type: EventTypes.ORDER_CREATION_FAILED,
        payload: {
          error: textKey,
          skus: productIds,
          email: email || storedEmail,
          currency,
          ...(source && {
            source,
          }),
        },
      });

      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath, error },
      });
    }

    if (payload) {
      if (statusCode === 201) {
        payload.sources = payload.sources.map(source => ({
          ...source,
          jointId: getPaymentSourceJointId({
            id: source.id,
            type: source.type,
            via: source.via,
          }),
        }));

        // Remove used card types in blacklist if it's within timeout secs.
        const blacklistPaymentSources =
          getOperationData(
            getState(),
            operationSelectPath,
            'blacklistPaymentSources'
          ) || {};
        const blacklistPaymentSourceKeys = Object.keys(blacklistPaymentSources);
        if (blacklistPaymentSourceKeys.length) {
          payload.sources = payload.sources.filter(source => {
            const orderTypeWithId = `${source.type}_${source.id}`;
            if (!blacklistPaymentSourceKeys.includes(orderTypeWithId)) {
              return true;
            }

            const currentUnixTimestamp = getCurrentUnixTimestamp({
              offsetSeconds: getTimestampOffset(getState(), Accuracy.SECOND),
            });
            const blacklistPaymentSourceUnixTimestamp =
              blacklistPaymentSources[orderTypeWithId].unixTimestamp;
            const diffSecs =
              currentUnixTimestamp - blacklistPaymentSourceUnixTimestamp;

            const blacklistTimeoutSecs =
              parseInt(
                getRemoteConfigData(
                  getState(),
                  DIAMOND_SHOP_METHOD_TYPES_BLACKLIST_TIMEOUT_SECS
                )
              ) || 0;

            if (diffSecs > blacklistTimeoutSecs) {
              return true;
            }

            return false;
          });
        }

        // Remove Apple Pay source on iOS12 PWA.
        if (getIsInPwa() && getIsIos12()) {
          payload.sources = payload.sources.filter(
            source => !getIsApplePay({ source })
          );
        }

        // Remove Tappay Apple Pay source if browser not support.
        const [tappayApplePaySource] = payload.sources.filter(source =>
          getIsTappayApplePay({ source })
        );
        if (tappayApplePaySource) {
          const tappayApplePayId = getRemoteConfigData(
            state,
            TAPPAY_APPLE_PAY_ID
          );
          const setupResult = await setupApplePayWithTappay({
            totalValue: payload.amount,
            currency: payload.currency,
            items: payload.items,
            tappayApplePayId,
          });
          if ('success' !== setupResult.status) {
            payload.sources = payload.sources.filter(
              source => !getIsTappayApplePay({ source })
            );
          } else {
            tappayApplePaySource.context = setupResult.payload;
          }
        }

        // Remove Apple Pay source if browser not support.
        if (!getIsSupportApplePay()) {
          payload.sources = payload.sources.filter(
            source => 'apple-pay' !== source.type
          );
        }

        let selectPaymentSourceJointId = getPaymentSourceJointId({
          id: source?.id,
          type: source?.type,
          via: source?.via,
        });

        if (!source) {
          // Default sequence:
          // 1. Apple Pay active card
          // 2. First saved card
          // 3. First non-saved card source

          // 2025 Mar default sequence:
          // 1. Apple Pay active card
          // 2. First saved card
          // 3. Previous payment source
          // 4. First payment source if mustSelectPaymentSource is true or is on tablet/desktop. Otherwise, null.

          const deviceWidth = getOperationData(getState(), ['device'], 'width');
          const isOnDesktopOrTablet = deviceWidth > breakpoint.mobile;

          let defaultPaymentSource = is2025MarShopUi
            ? payload.sources.find(source => source.last_used) ||
              (isOnDesktopOrTablet || mustSelectPaymentSource
                ? payload.sources[0]
                : null)
            : payload.sources[0];

          const [firstSavedCardSource] = payload.sources.filter(
            source => 'card' === source.type && source?.context?.last4
          );
          defaultPaymentSource = firstSavedCardSource || defaultPaymentSource;
          if (tappayApplePaySource?.context?.canMakePaymentWithActiveCard) {
            defaultPaymentSource = tappayApplePaySource;
          }

          const ecpayApplePayMerchantId = getRemoteConfigData(
            getState(),
            ECPAY_APPLE_PAY_MERCHANT_ID
          );
          if (ecpayApplePayMerchantId) {
            const hasActiveApplePayCard = await checkApplePayActiveCard(
              ecpayApplePayMerchantId
            );
            const [ecpayApplePay] = payload.sources.filter(source =>
              getIsEcpayApplePay({ source })
            );
            if (hasActiveApplePayCard && ecpayApplePay) {
              defaultPaymentSource = ecpayApplePay;
            }
          }

          // Should show express payment if there is no default payment source
          payload['shouldShowExpressPayment'] = !defaultPaymentSource;
          selectPaymentSourceJointId = defaultPaymentSource?.jointId;

          if (is2025MarShopUi) {
            // Need to save order to make sure the order's payment sources is available.
            dispatch({
              type: ADD_ORDERS,
              payload: { orders: { [payload.id]: payload } },
            });
            dispatch({
              type: MERGE_OPERATION_DATA,
              payload: {
                selectPath: operationSelectPath,
                data: {
                  submitOrderId: payload.id,
                },
              },
            });
            if (!defaultPaymentSource) {
              return dispatch({
                type: SET_NETWORKING_IDLE,
                payload: { selectPath },
              });
            }
          }

          return dispatch(
            submitOrder({
              shopId,
              productIds,
              currency,
              source: {
                id: defaultPaymentSource?.id,
                type: defaultPaymentSource?.type,
                via: defaultPaymentSource?.via,
              },
            })
          );
        } else {
          // Get apple pay merchant token
          if (getIsEcpayApplePay({ source })) {
            const applePaySource = payload.source;
            selectPaymentSourceJointId = getPaymentSourceJointId({
              id: applePaySource?.id,
              type: applePaySource?.type,
              via: applePaySource?.via,
            });
            const ecpayToken = applePaySource['id']?.split(':')?.[1];
            if (ecpayToken) {
              dispatch({
                type: MERGE_ORDER_DATA,
                payload: {
                  order: {
                    id: payload.id,
                    ecpayApplePayToken: ecpayToken,
                  },
                },
              });
              dispatch(
                addModal({
                  id: `${shopId}.EcpayApplePay`,
                  isHigherThanAll: true,
                })
              );
            }
          } else if (getIsNineoneappApplePay({ source })) {
            dispatch(
              addModal({
                id: `${shopId}.NineoneAppApplePay`,
                isHigherThanAll: true,
              })
            );
          }
        }
        // `isFromExpressPayment` is set by express payment related actions (Shop2025MarFooter mounted and Shop2025MarExpressPayButton)
        // When true, forces the express payment UI to be displayed
        payload['shouldShowExpressPayment'] = isFromExpressPayment;

        await Promise.allSettled([
          dispatch({
            type: ADD_ORDERS,
            payload: { orders: { [payload.id]: payload } },
          }),
          dispatch({
            type: MERGE_OPERATION_DATA,
            payload: {
              selectPath: operationSelectPath,
              data: {
                submitOrderId: payload.id,
                selectPaymentSourceJointId,
              },
            },
          }),
        ]);
      }
      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath },
      });
    }
  };

export default submitOrder;
